import type { FleetVehicle } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import { fleetPanelClassName } from '@/products/fleet/features/fleet-panel';
import { FleetVehicleDeleteButton } from '@/products/fleet/features/fleet-vehicle-delete-button';
import { FleetVehicleForm } from '@/products/fleet/features/fleet-vehicle-form';
import { useRetireFleetVehicle, useUpdateFleetVehicle } from '@/products/fleet/features/use-fleet';
import { FLEET_VEHICLES_PATH } from '@/products/fleet/fleet-paths';

export function FleetVehicleStammTab({
  vehicle,
  manage,
  telematicsManage,
}: {
  vehicle: FleetVehicle;
  manage: boolean;
  telematicsManage: boolean;
}) {
  const navigate = useNavigate();
  const update = useUpdateFleetVehicle(vehicle.id);
  const retire = useRetireFleetVehicle(vehicle.id);
  const [retireOpen, setRetireOpen] = useState(false);
  const vehicleLabel = `${vehicle.licensePlate} (${vehicle.make} ${vehicle.model})`;

  if (!manage && !telematicsManage) {
    return (
      <dl className={`${fleetPanelClassName} grid gap-2 text-sm sm:grid-cols-2`}>
        <div>
          <dt className="text-muted-foreground">Kennzeichen</dt>
          <dd>{vehicle.licensePlate}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Fahrzeug</dt>
          <dd>
            {vehicle.make} {vehicle.model}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Klasse</dt>
          <dd>{vehicle.requiredLicenseClass}</dd>
        </div>
        {vehicle.telematicsRef ? (
          <div>
            <dt className="text-muted-foreground">Telematik-Referenz</dt>
            <dd>{vehicle.telematicsRef}</dd>
          </div>
        ) : null}
        {vehicle.tankCapacityLiters != null ? (
          <div>
            <dt className="text-muted-foreground">Tankvolumen</dt>
            <dd>{vehicle.tankCapacityLiters} Liter</dd>
          </div>
        ) : null}
      </dl>
    );
  }

  return (
    <div className="space-y-4">
      <FleetVehicleForm
        vehicle={vehicle}
        pending={update.isPending}
        telematicsManage={telematicsManage}
        manage={manage}
        onUpdate={async (body) => {
          await update.mutateAsync(body);
          toast.success('Fahrzeug gespeichert.');
        }}
      />
      <div className="flex flex-wrap gap-2">
        {manage ? (
          <FleetVehicleDeleteButton
            vehicle={vehicle}
            onDeleted={() => navigate(FLEET_VEHICLES_PATH)}
          />
        ) : null}
        {manage && vehicle.operationalStatus !== 'retired' ? (
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            disabled={retire.isPending}
            onClick={() => setRetireOpen(true)}
          >
            Stilllegen
          </Button>
        ) : null}
      </div>
      <AlertDialog
        open={retireOpen}
        onOpenChange={(next) => {
          if (!next && !retire.isPending) {
            setRetireOpen(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{vehicleLabel} stilllegen?</AlertDialogTitle>
            <AlertDialogDescription>
              Das Fahrzeug verschwindet aus Buchung, Zuordnung und Erinnerung. Prüfungen, Dokumente
              und Fahrten bleiben lesbar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={retire.isPending}>Abbrechen</AlertDialogCancel>
            <Button
              type="button"
              className="min-h-11"
              disabled={retire.isPending}
              onClick={() => {
                void retire.mutateAsync().then(
                  () => {
                    setRetireOpen(false);
                    toast.success(`${vehicleLabel} wurde stillgelegt.`);
                  },
                  (error: unknown) => {
                    toast.error(
                      error instanceof ApiRequestError
                        ? error.message
                        : 'Das Fahrzeug konnte nicht stillgelegt werden.',
                    );
                  },
                );
              }}
            >
              {retire.isPending ? 'Wird stillgelegt …' : 'Stilllegen'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
